import React, { useEffect, useState } from "react";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Compass (#191) — full-document JSON viewer with a collapsible tree.
//
// Objects and arrays are collapsible/expandable. Default state: the root is
// expanded one level (you see the doc's top-level fields) and every nested
// object/array is COLLAPSED. "Expand all" / "Collapse all" buttons override.
// Selecting a different row resets to the default. Convex docs are decoded
// (the `»`/`$` key codec reversed) before they reach here. READ ONLY in
// Phase 1 (Phase 2 adds inline edit routed through the real Mongo + EventLog
// path so admin edits land in Time Machine).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

function safeStringify(doc) {
  try {
    return JSON.stringify(doc, null, 2);
  } catch (e) {
    return `// Could not stringify document: ${e.message}`;
  }
}

function isContainer(v) {
  return v !== null && typeof v === "object";
}

// Render a primitive (string/number/bool/null) with a subtle type color.
function Primitive({ value }) {
  let color = "#d4d4d4";
  if (typeof value === "string") color = "#9cdcfe";
  else if (typeof value === "number") color = "#b5cea8";
  else if (typeof value === "boolean") color = "#569cd6";
  else if (value === null) color = "#808080";
  return <span style={{ color }}>{JSON.stringify(value)}</span>;
}

function summaryLabel(value) {
  if (Array.isArray(value)) {
    return `Array(${value.length})`;
  }
  const n = Object.keys(value).length;
  return `{${n} field${n === 1 ? "" : "s"}}`;
}

// One node. `forceExpand` (true|false|null) seeds the initial open state; the
// whole tree is remounted (via a key bump in the parent) when it changes, so
// each node re-initializes. null → root open, descendants collapsed.
function JsonNode({ label, value, depth, forceExpand }) {
  const container = isContainer(value);
  const initialOpen = forceExpand !== null ? forceExpand : depth === 0;
  const [open, setOpen] = useState(initialOpen);

  const indent = { paddingLeft: depth === 0 ? 0 : 14 };

  if (!container) {
    return (
      <div style={indent}>
        {label !== null && <span style={{ color: "#c586c0" }}>{label}: </span>}
        <Primitive value={value} />
      </div>
    );
  }

  const entries = Array.isArray(value)
    ? value.map((v, i) => [i, v])
    : Object.entries(value);

  return (
    <div style={indent}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        <span style={{ display: "inline-block", width: 12, color: "#808080" }}>
          {entries.length === 0 ? "" : open ? "▾" : "▸"}
        </span>
        {label !== null && <span style={{ color: "#c586c0" }}>{label}: </span>}
        <span style={{ color: "#808080" }}>{summaryLabel(value)}</span>
      </div>
      {open &&
        entries.map(([k, v]) => (
          <JsonNode
            key={k}
            label={String(k)}
            value={v}
            depth={depth + 1}
            forceExpand={forceExpand}
          />
        ))}
    </div>
  );
}

function CompassDocViewer({ doc }) {
  const [copied, setCopied] = useState(false);
  const [treeKey, setTreeKey] = useState(0);
  const [forceExpand, setForceExpand] = useState(null);

  // Reset to default (root open, descendants collapsed) whenever the selected
  // document changes.
  useEffect(() => {
    setForceExpand(null);
    setTreeKey((k) => k + 1);
  }, [doc]);

  if (!doc) {
    return (
      <div className="text-skin-link text-sm p-4 italic">
        Select a row to view the full document.
      </div>
    );
  }

  const handleCopy = () => {
    try {
      navigator.clipboard?.writeText(safeStringify(doc));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      /* clipboard not available — no-op */
    }
  };

  const expandAll = () => {
    setForceExpand(true);
    setTreeKey((k) => k + 1);
  };
  const collapseAll = () => {
    setForceExpand(false);
    setTreeKey((k) => k + 1);
  };

  const btn =
    "text-xs border-2 border-skin-base rounded px-2 py-1 hover:opacity-75";

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <span className="font-medium">Document</span>
        <div className="flex gap-2">
          <button type="button" onClick={expandAll} className={btn}>
            Expand all
          </button>
          <button type="button" onClick={collapseAll} className={btn}>
            Collapse all
          </button>
          <button type="button" onClick={handleCopy} className={btn}>
            {copied ? "Copied ✓" : "Copy JSON"}
          </button>
        </div>
      </div>
      <div
        className="flex-1 min-h-0 overflow-auto text-xs p-3 rounded"
        style={{
          backgroundColor: "rgba(0,0,0,0.25)",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          lineHeight: 1.5,
        }}
      >
        <JsonNode
          key={treeKey}
          label={null}
          value={doc}
          depth={0}
          forceExpand={forceExpand}
        />
      </div>
    </div>
  );
}

export default CompassDocViewer;
